import {
    Autocomplete,
    AutocompleteRenderInputParams,
    ListItemAvatar,
    ListItemText,
    Stack,
    TextField
} from "@mui/material";
import React, {useCallback, useMemo} from "react";
import {WorkSite} from "../../api/Model";
import {TextFieldProps} from "@mui/material/TextField/TextField";
import {autocompleteChangeEvent, SetState} from "../../util/FormHandlers";
import {SiteChip} from "../site-chip/SiteChip";
import {idEqFn, idFn} from "../../util/FunctionUtil";
import {useRemoteValue} from "../../util/RemoteValue";
import {useWorkSiteStore} from "../../api/WorkSiteStore";


const optionLabel = (option: WorkSite) => option.badge || option.name;
const renderOption = ({key, ...props}: React.HTMLAttributes<HTMLLIElement> & { key: any }, site: WorkSite) => (
    <Stack key={key} component="li" direction="row" spacing={2} justifyItems="stretch" {...props}>
        <ListItemText primary={site.name} slotProps={{primary: {sx: {textWrap: 'nowrap'}}}}/>
        {site.badge && (
            <ListItemAvatar>
                <SiteChip siteId={site.id}/>
            </ListItemAvatar>
        )}
    </Stack>
);

export function SiteSelect(
    {
        site,
        setSite,
        ...props
    }: {
        site: WorkSite | null,
        setSite: SetState<WorkSite | null>,
    } & TextFieldProps
) {
    const workSites = useWorkSiteStore(state => state.remoteValue);
    const options = useRemoteValue(workSites, []);

    const handleWorkSiteChange = useMemo(
        () => autocompleteChangeEvent((newWorkSite: WorkSite | null) => setSite(newWorkSite)),
        [setSite],
    );

    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => (
            <TextField {...params} {...props} />
        ), [props]);

    return (
        <Autocomplete<WorkSite>
            options={options}
            renderInput={renderInput}
            renderOption={renderOption}
            getOptionKey={idFn}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={idEqFn}
            value={site || null}
            disableClearable={false}
            onChange={handleWorkSiteChange}

            slotProps={{paper: {sx: {width: 'fit-content'}}}}
        />
    );
}

export function SitesSelect({
    sites,
    setSites,
    ...props
}: {
    sites: WorkSite[],
    setSites: SetState<WorkSite[]>,
} & TextFieldProps) {
    const workSites = useWorkSiteStore(state => state.remoteValue);
    const options = useRemoteValue(workSites, []);
    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => <TextField {...params} {...props}/>,
        [props],
    );

    return (
        <Autocomplete<WorkSite, true>
            multiple
            options={options}
            renderInput={renderInput}
            renderOption={renderOption}
            getOptionKey={idFn}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={idEqFn}
            value={sites}
            onChange={(_, value) => setSites(value)}
            slotProps={{paper: {sx: {width: 'fit-content'}}}}
        />
    );
}
